import { c as _c } from "react/compiler-runtime";
import Pause from "@mui/icons-material/Pause";
import PlayArrow from "@mui/icons-material/PlayArrow";
import Replay from "@mui/icons-material/Replay";
import SkipNext from "@mui/icons-material/SkipNext";
import SkipPrevious from "@mui/icons-material/SkipPrevious";
import { Alert, alpha, Box, IconButton, Stack, Typography, useTheme } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Provider } from "react-redux";
import { callstackSlice, selectCallstack, selectCallstackFrameIndex, selectCallstackIsPlaying, selectCallstackIsReady, selectCallstackLength, selectIsLastFrame, selectIsRootFrame } from "#/features/callstack/model/callstackSlice";
import { CompactCallstackList } from "#/features/callstack/ui/CompactCallstackList";
import { createLandingHeroPreviewStore, type LandingHeroPreviewStore } from "#/features/homePage/model/landingHeroPreviewStore";
import { usePlayerControls } from "#/features/treeViewer/hooks";
import { getPlaybackStepGroups, getPlaybackStepIndex } from "#/features/treeViewer/lib";
import { TreeViewer } from "#/features/treeViewer/ui/TreeViewer";
import type { TranslationFunctions } from "#/i18n/i18n-types";
import { useAppDispatch, useAppSelector } from "#/store/hooks";
type HomeLandingHeroPreviewRuntimeProps = {
  LL: TranslationFunctions;
};
const PLAYBACK_INTERVAL_MS = 600;
const REPLAY_RESET_PAUSE_MS = 220;
type LandingHeroPreviewRuntimeState = {
  store: LandingHeroPreviewStore;
  errorMessage: null;
} | {
  store: null;
  errorMessage: string;
};
const getPreviewRuntimeErrorMessage = (error: unknown): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return "";
};
const HomeLandingHeroPreviewRuntimeInner: React.FC<HomeLandingHeroPreviewRuntimeProps> = t0 => {
  const $ = _c(152);
  const {
    LL
  } = t0;
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const hasAutoStartedRef = useRef(false);
  const hasManualOverrideRef = useRef(false);
  const replayTimeoutRef = useRef(null);
  const replayRestartTimeoutRef = useRef(null);
  const {
    handleReset,
    handlePlay,
    handleReplay,
    handleStepBack,
    handleStepForward
  } = usePlayerControls();
  const isReady = useAppSelector(selectCallstackIsReady);
  const callstack = useAppSelector(selectCallstack);
  const isPlaying = useAppSelector(selectCallstackIsPlaying);
  const frameIndex = useAppSelector(selectCallstackFrameIndex);
  const callstackLength = useAppSelector(selectCallstackLength);
  const isLastFrame = useAppSelector(selectIsLastFrame);
  const isRootFrame = useAppSelector(selectIsRootFrame);
  let t1;
  if ($[0] !== callstack.frames) {
    t1 = getPlaybackStepGroups(callstack.frames, {
      forCallstackDisplay: true
    });
    $[0] = callstack.frames;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const playbackStepGroups = t1;
  const activePlaybackStepIndex = getPlaybackStepIndex(playbackStepGroups, frameIndex);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (replayTimeoutRef.current !== null) {
        window.clearTimeout(replayTimeoutRef.current);
        replayTimeoutRef.current = null;
      }
      if (replayRestartTimeoutRef.current !== null) {
        window.clearTimeout(replayRestartTimeoutRef.current);
        replayRestartTimeoutRef.current = null;
      }
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const clearReplayTimers = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      if (replayTimeoutRef.current !== null) {
        window.clearTimeout(replayTimeoutRef.current);
        replayTimeoutRef.current = null;
      }
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const clearAutoReplayEndDelayOnly = t3;
  let t4;
  if ($[4] !== dispatch) {
    t4 = () => {
      clearReplayTimers();
      hasManualOverrideRef.current = true;
      dispatch(callstackSlice.actions.setIsPlaying(false));
    };
    $[4] = dispatch;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const stopAutoplay = t4;
  let t5;
  if ($[6] !== handleStepBack || $[7] !== stopAutoplay) {
    t5 = () => {
      stopAutoplay();
      handleStepBack();
    };
    $[6] = handleStepBack;
    $[7] = stopAutoplay;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const handleManualStepBack = t5;
  let t6;
  if ($[9] !== handlePlay) {
    t6 = () => {
      hasManualOverrideRef.current = true;
      handlePlay();
    };
    $[9] = handlePlay;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const handleManualPlayToggle = t6;
  let t7;
  if ($[11] !== handleReplay || $[12] !== stopAutoplay) {
    t7 = () => {
      stopAutoplay();
      handleReplay();
    };
    $[11] = handleReplay;
    $[12] = stopAutoplay;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const handleManualReplay = t7;
  let t8;
  if ($[14] !== handleStepForward || $[15] !== stopAutoplay) {
    t8 = () => {
      stopAutoplay();
      handleStepForward();
    };
    $[14] = handleStepForward;
    $[15] = stopAutoplay;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const handleManualStepForward = t8;
  let t10;
  let t9;
  if ($[17] !== callstackLength || $[18] !== handlePlay || $[19] !== isReady) {
    t9 = () => {
      if (!isReady || callstackLength === 0 || hasAutoStartedRef.current || hasManualOverrideRef.current) {
        return;
      }
      hasAutoStartedRef.current = true;
      handlePlay();
    };
    t10 = [callstackLength, handlePlay, isReady];
    $[17] = callstackLength;
    $[18] = handlePlay;
    $[19] = isReady;
    $[20] = t10;
    $[21] = t9;
  } else {
    t10 = $[20];
    t9 = $[21];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[22] !== dispatch || $[23] !== handleReset || $[24] !== isLastFrame || $[25] !== isPlaying || $[26] !== isReady) {
    t11 = () => {
      if (!isReady || !hasAutoStartedRef.current || hasManualOverrideRef.current || isPlaying || !isLastFrame) {
        return;
      }
      replayTimeoutRef.current = window.setTimeout(() => {
        replayTimeoutRef.current = null;
        handleReset();
        replayRestartTimeoutRef.current = window.setTimeout(() => {
          replayRestartTimeoutRef.current = null;
          if (!hasManualOverrideRef.current) {
            dispatch(callstackSlice.actions.setIsPlaying(true));
          }
        }, REPLAY_RESET_PAUSE_MS);
      }, 900);
      return clearAutoReplayEndDelayOnly;
    };
    t12 = [clearAutoReplayEndDelayOnly, dispatch, handleReset, isLastFrame, isPlaying, isReady];
    $[22] = dispatch;
    $[23] = handleReset;
    $[24] = isLastFrame;
    $[25] = isPlaying;
    $[26] = isReady;
    $[27] = t11;
    $[28] = t12;
  } else {
    t11 = $[27];
    t12 = $[28];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => () => {
      clearReplayTimers();
    };
    t14 = [clearReplayTimers];
    $[29] = t13;
    $[30] = t14;
  } else {
    t13 = $[29];
    t14 = $[30];
  }
  useEffect(t13, t14);
  let t15;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      minWidth: 0
    };
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      minWidth: 0
    };
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== LL) {
    t17 = LL.HOME_PILLAR_REPLAY_TITLE();
    $[33] = LL;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== t17) {
    t18 = <Typography variant="subtitle2" color="text.secondary">{t17}</Typography>;
    $[35] = t17;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== LL || $[38] !== activePlaybackStepIndex || $[39] !== callstackLength || $[40] !== playbackStepGroups.length) {
    t19 = LL.HOME_PREVIEW_STEP_PROGRESS({
      step: Math.max(activePlaybackStepIndex + 1, 0),
      total: playbackStepGroups.length || callstackLength
    });
    $[37] = LL;
    $[38] = activePlaybackStepIndex;
    $[39] = callstackLength;
    $[40] = playbackStepGroups.length;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== t19) {
    t20 = <Typography variant="caption" color="text.secondary">{t19}</Typography>;
    $[42] = t19;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== t18 || $[45] !== t20) {
    t21 = <Stack direction="row" justifyContent="space-between" alignItems="center" sx={t16}>{t18}{t20}</Stack>;
    $[44] = t18;
    $[45] = t20;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== theme.appDesign.surfaceLowest) {
    t22 = alpha(theme.appDesign.surfaceLowest, 0.92);
    $[47] = theme.appDesign.surfaceLowest;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== theme.appDesign.outline) {
    t23 = alpha(theme.appDesign.outline, 0.12);
    $[49] = theme.appDesign.outline;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  const t24 = `1px solid ${t23}`;
  let t25;
  if ($[51] !== t22 || $[52] !== t24) {
    t25 = {
      position: "relative",
      minHeight: 212,
      borderRadius: 3,
      bgcolor: t22,
      border: t24,
      overflow: "hidden"
    };
    $[51] = t22;
    $[52] = t24;
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  let t26;
  if ($[54] !== theme.appDesign.accent) {
    t26 = alpha(theme.appDesign.accent, 0.22);
    $[54] = theme.appDesign.accent;
    $[55] = t26;
  } else {
    t26 = $[55];
  }
  const t27 = `radial-gradient(circle at 50% 20%, ${t26} 0%, transparent 46%)`;
  let t28;
  if ($[56] !== t27) {
    t28 = <Box sx={{
      position: "absolute",
      inset: 0,
      background: t27,
      pointerEvents: "none"
    }} />;
    $[56] = t27;
    $[57] = t28;
  } else {
    t28 = $[57];
  }
  let t29;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <Box sx={{
      position: "relative",
      zIndex: 1,
      height: "100%",
      px: 1,
      py: 1.25
    }}><TreeViewer playbackInterval={PLAYBACK_INTERVAL_MS} binaryTreeAlign="center" /></Box>;
    $[58] = t29;
  } else {
    t29 = $[58];
  }
  let t30;
  if ($[59] !== t25 || $[60] !== t28) {
    t30 = <Box sx={t25}>{t28}{t29}</Box>;
    $[59] = t25;
    $[60] = t28;
    $[61] = t30;
  } else {
    t30 = $[61];
  }
  let t31;
  if ($[62] !== theme.appDesign.surfaceLowest) {
    t31 = alpha(theme.appDesign.surfaceLowest, 0.86);
    $[62] = theme.appDesign.surfaceLowest;
    $[63] = t31;
  } else {
    t31 = $[63];
  }
  let t32;
  if ($[64] !== theme.appDesign.outline) {
    t32 = alpha(theme.appDesign.outline, 0.1);
    $[64] = theme.appDesign.outline;
    $[65] = t32;
  } else {
    t32 = $[65];
  }
  const t33 = `1px solid ${t32}`;
  let t34;
  if ($[66] !== t31 || $[67] !== t33) {
    t34 = {
      borderRadius: 3,
      bgcolor: t31,
      border: t33,
      overflow: "hidden"
    };
    $[66] = t31;
    $[67] = t33;
    $[68] = t34;
  } else {
    t34 = $[68];
  }
  let t35;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = {
      px: 1.5,
      py: 1.25
    };
    $[69] = t35;
  } else {
    t35 = $[69];
  }
  let t36;
  if ($[70] !== LL) {
    t36 = LL.CALLSTACK();
    $[70] = LL;
    $[71] = t36;
  } else {
    t36 = $[71];
  }
  let t37;
  if ($[72] !== t36) {
    t37 = <Stack direction="row" spacing={1} alignItems="center" sx={t35}><Typography variant="subtitle2" color="text.secondary">{t36}</Typography></Stack>;
    $[72] = t36;
    $[73] = t37;
  } else {
    t37 = $[73];
  }
  let t38;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <CompactCallstackList height={262} />;
    $[74] = t38;
  } else {
    t38 = $[74];
  }
  let t39;
  if ($[75] !== t34 || $[76] !== t37) {
    t39 = <Box sx={t34}>{t37}{t38}</Box>;
    $[75] = t34;
    $[76] = t37;
    $[77] = t39;
  } else {
    t39 = $[77];
  }
  let t40;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = {
      xs: "column",
      sm: "row"
    };
    $[78] = t40;
  } else {
    t40 = $[78];
  }
  let t41;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = {
      minWidth: 0
    };
    $[79] = t41;
  } else {
    t41 = $[79];
  }
  let t42;
  if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = {
      xs: "flex-end",
      sm: "flex-start"
    };
    $[80] = t42;
  } else {
    t42 = $[80];
  }
  const t43 = !isReady || isRootFrame;
  let t44;
  if ($[81] !== LL) {
    t44 = LL.HOME_PREVIEW_STEP_BACK();
    $[81] = LL;
    $[82] = t44;
  } else {
    t44 = $[82];
  }
  let t45;
  if ($[83] !== LL) {
    t45 = LL.HOME_PREVIEW_STEP_BACK();
    $[83] = LL;
    $[84] = t45;
  } else {
    t45 = $[84];
  }
  let t46;
  if ($[85] !== theme.appDesign.surfaceHigh) {
    t46 = alpha(theme.appDesign.surfaceHigh, 0.9);
    $[85] = theme.appDesign.surfaceHigh;
    $[86] = t46;
  } else {
    t46 = $[86];
  }
  let t47;
  if ($[87] !== t46) {
    t47 = {
      bgcolor: t46
    };
    $[87] = t46;
    $[88] = t47;
  } else {
    t47 = $[88];
  }
  let t48;
  if ($[89] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = <SkipPrevious fontSize="small" />;
    $[89] = t48;
  } else {
    t48 = $[89];
  }
  let t49;
  if ($[90] !== handleManualStepBack || $[91] !== t43 || $[92] !== t44 || $[93] !== t45 || $[94] !== t47) {
    t49 = <IconButton size="small" onClick={handleManualStepBack} disabled={t43} aria-label={t44} title={t45} sx={t47}>{t48}</IconButton>;
    $[90] = handleManualStepBack;
    $[91] = t43;
    $[92] = t44;
    $[93] = t45;
    $[94] = t47;
    $[95] = t49;
  } else {
    t49 = $[95];
  }
  const t50 = isLastFrame && !isPlaying ? handleManualReplay : handleManualPlayToggle;
  const t51 = !callstackLength;
  let t52;
  if ($[96] !== LL || $[97] !== isLastFrame || $[98] !== isPlaying) {
    t52 = isLastFrame && !isPlaying ? LL.REPLAY() : isPlaying ? LL.HOME_LANDING_PREVIEW_PAUSE() : LL.HOME_LANDING_PREVIEW_PLAY();
    $[96] = LL;
    $[97] = isLastFrame;
    $[98] = isPlaying;
    $[99] = t52;
  } else {
    t52 = $[99];
  }
  let t53;
  if ($[100] !== LL || $[101] !== isLastFrame || $[102] !== isPlaying) {
    t53 = isLastFrame && !isPlaying ? LL.REPLAY() : isPlaying ? LL.HOME_LANDING_PREVIEW_PAUSE() : LL.HOME_LANDING_PREVIEW_PLAY();
    $[100] = LL;
    $[101] = isLastFrame;
    $[102] = isPlaying;
    $[103] = t53;
  } else {
    t53 = $[103];
  }
  let t54;
  if ($[104] !== theme.appDesign.accent) {
    t54 = alpha(theme.appDesign.accent, 0.14);
    $[104] = theme.appDesign.accent;
    $[105] = t54;
  } else {
    t54 = $[105];
  }
  let t55;
  if ($[106] !== t54 || $[107] !== theme.appDesign.accentSoft) {
    t55 = {
      bgcolor: t54,
      color: theme.appDesign.accentSoft
    };
    $[106] = t54;
    $[107] = theme.appDesign.accentSoft;
    $[108] = t55;
  } else {
    t55 = $[108];
  }
  let t56;
  if ($[109] !== isLastFrame || $[110] !== isPlaying) {
    t56 = isLastFrame && !isPlaying ? <Replay fontSize="small" /> : isPlaying ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />;
    $[109] = isLastFrame;
    $[110] = isPlaying;
    $[111] = t56;
  } else {
    t56 = $[111];
  }
  let t57;
  if ($[112] !== t50 || $[113] !== t51 || $[114] !== t52 || $[115] !== t53 || $[116] !== t55 || $[117] !== t56) {
    t57 = <IconButton size="small" onClick={t50} disabled={t51} aria-label={t52} title={t53} sx={t55}>{t56}</IconButton>;
    $[112] = t50;
    $[113] = t51;
    $[114] = t52;
    $[115] = t53;
    $[116] = t55;
    $[117] = t56;
    $[118] = t57;
  } else {
    t57 = $[118];
  }
  const t58 = !isReady || isLastFrame;
  let t59;
  if ($[119] !== LL) {
    t59 = LL.HOME_PREVIEW_STEP_FORWARD();
    $[119] = LL;
    $[120] = t59;
  } else {
    t59 = $[120];
  }
  let t60;
  if ($[121] !== LL) {
    t60 = LL.HOME_PREVIEW_STEP_FORWARD();
    $[121] = LL;
    $[122] = t60;
  } else {
    t60 = $[122];
  }
  let t61;
  if ($[123] !== theme.appDesign.surfaceHigh) {
    t61 = alpha(theme.appDesign.surfaceHigh, 0.9);
    $[123] = theme.appDesign.surfaceHigh;
    $[124] = t61;
  } else {
    t61 = $[124];
  }
  let t62;
  if ($[125] !== t61) {
    t62 = {
      bgcolor: t61
    };
    $[125] = t61;
    $[126] = t62;
  } else {
    t62 = $[126];
  }
  let t63;
  if ($[127] === Symbol.for("react.memo_cache_sentinel")) {
    t63 = <SkipNext fontSize="small" />;
    $[127] = t63;
  } else {
    t63 = $[127];
  }
  let t64;
  if ($[128] !== handleManualStepForward || $[129] !== t58 || $[130] !== t59 || $[131] !== t60 || $[132] !== t62) {
    t64 = <IconButton size="small" onClick={handleManualStepForward} disabled={t58} aria-label={t59} title={t60} sx={t62}>{t63}</IconButton>;
    $[128] = handleManualStepForward;
    $[129] = t58;
    $[130] = t59;
    $[131] = t60;
    $[132] = t62;
    $[133] = t64;
  } else {
    t64 = $[133];
  }
  let t65;
  if ($[134] !== t49 || $[135] !== t57 || $[136] !== t64) {
    t65 = <Stack direction="row" spacing={0.5} justifyContent={t42}>{t49}{t57}{t64}</Stack>;
    $[134] = t49;
    $[135] = t57;
    $[136] = t64;
    $[137] = t65;
  } else {
    t65 = $[137];
  }
  let t66;
  if ($[138] === Symbol.for("react.memo_cache_sentinel")) {
    t66 = {
      sm: 0.5
    };
    $[138] = t66;
  } else {
    t66 = $[138];
  }
  let t67;
  if ($[139] === Symbol.for("react.memo_cache_sentinel")) {
    t67 = {
      ml: t66,
      lineHeight: 1.6,
      maxWidth: {
        xs: "100%",
        sm: 220
      },
      textAlign: {
        xs: "right",
        sm: "left"
      }
    };
    $[139] = t67;
  } else {
    t67 = $[139];
  }
  let t68;
  if ($[140] !== LL) {
    t68 = LL.HOME_PILLAR_VIS_BODY();
    $[140] = LL;
    $[141] = t68;
  } else {
    t68 = $[141];
  }
  let t69;
  if ($[142] !== t68) {
    t69 = <Typography variant="caption" color="text.secondary" sx={t67}>{t68}</Typography>;
    $[142] = t68;
    $[143] = t69;
  } else {
    t69 = $[143];
  }
  let t70;
  if ($[144] !== t65 || $[145] !== t69) {
    t70 = <Stack direction={t40} spacing={1} alignItems="center" sx={t41}>{t65}{t69}</Stack>;
    $[144] = t65;
    $[145] = t69;
    $[146] = t70;
  } else {
    t70 = $[146];
  }
  let t71;
  if ($[147] !== t21 || $[148] !== t30 || $[149] !== t39 || $[150] !== t70) {
    t71 = <Stack spacing={1.5} sx={t15}>{t21}{t30}{t39}{t70}</Stack>;
    $[147] = t21;
    $[148] = t30;
    $[149] = t39;
    $[150] = t70;
    $[151] = t71;
  } else {
    t71 = $[151];
  }
  return t71;
};
export const HomeLandingHeroPreviewRuntime: React.FC<HomeLandingHeroPreviewRuntimeProps> = t0 => {
  const $ = _c(17);
  const {
    LL
  } = t0;
  const [runtimeState] = useState(_temp);
  if (!runtimeState.store) {
    let t1;
    if ($[0] !== LL) {
      t1 = LL.HOME_LANDING_PREVIEW_LOAD_FAILED();
      $[0] = LL;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = <Typography variant="subtitle2">{t1}</Typography>;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    let t3;
    if ($[4] !== LL || $[5] !== runtimeState.errorMessage) {
      t3 = runtimeState.errorMessage || LL.HOME_LANDING_PREVIEW_ERROR_UNEXPECTED();
      $[4] = LL;
      $[5] = runtimeState.errorMessage;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    let t4;
    if ($[7] !== t3) {
      t4 = <Typography variant="caption">{t3}</Typography>;
      $[7] = t3;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    let t5;
    if ($[9] !== t2 || $[10] !== t4) {
      t5 = <Alert severity="error" variant="outlined">{t2}{t4}</Alert>;
      $[9] = t2;
      $[10] = t4;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    return t5;
  }
  let t1;
  if ($[12] !== LL) {
    t1 = <HomeLandingHeroPreviewRuntimeInner LL={LL} />;
    $[12] = LL;
    $[13] = t1;
  } else {
    t1 = $[13];
  }
  let t2;
  if ($[14] !== runtimeState.store || $[15] !== t1) {
    t2 = <Provider store={runtimeState.store}>{t1}</Provider>;
    $[14] = runtimeState.store;
    $[15] = t1;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  return t2;
};
function _temp() {
  ;
  try {
    return {
      store: createLandingHeroPreviewStore(),
      errorMessage: null
    };
  } catch (t0) {
    const error = t0;
    return {
      store: null,
      errorMessage: getPreviewRuntimeErrorMessage(error)
    };
  }
}